import React, { useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { gtmOfferClick } from '../../utils/gtm';

const trackEvent = (name, props) => {
  if (window.plausible) window.plausible(name, { props });
};

const PARTNER_CONFIG = {
  tangerine: {
    logo: '/logos/tangerine.svg',
    alt: 'Tangerine',
    applyUrl: 'https://www.tangerine.ca/en/landing-page/switch-to-tangerine?orangeKey=60870716S1&utm_source=finmaple&utm_medium=referral',
    applyLabel: 'Abrir cuenta en Tangerine',
  },
};

const ReferralCodeBlock = ({ title, description, code, copyButtonLabel, copiedLabel, disclosure, partner }) => {
  const [copied, setCopied] = useState(false);
  const config = partner ? PARTNER_CONFIG[partner] : null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      trackEvent('Referral Code Copied', { code, partner: partner || 'unknown' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const codeEl = document.getElementById('referral-code');
      if (codeEl) {
        const range = document.createRange();
        range.selectNodeContents(codeEl);
        window.getSelection().removeAllRanges();
        window.getSelection().addRange(range);
      }
    }
  };

  return (
    <Box
      sx={{
        mt: 2.5,
        p: { xs: '16px', md: '20px 24px' },
        bgcolor: '#F7F9FC',
        border: '1px solid #E5E7EB',
        borderRadius: '10px',
      }}
    >
      {/* Header: logo + title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        {config?.logo && (
          <Box
            sx={{
              width: 48,
              height: 32,
              borderRadius: '6px',
              bgcolor: '#FFFFFF',
              border: '1px solid #E5E7EB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              p: 0.5,
            }}
          >
            <Box
              component="img"
              src={config.logo}
              alt={config.alt}
              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          </Box>
        )}
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1B2A4A' }}>
          {title}
        </Typography>
      </Box>

      <Typography sx={{ fontSize: '13px', color: '#6B7280', lineHeight: 1.5, mb: 2 }}>
        {description}
      </Typography>

      {/* Code + copy row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: '10px 14px',
          bgcolor: '#FFFFFF',
          border: '1px dashed #D1D5DB',
          borderRadius: '8px',
          mb: 2,
        }}
      >
        <Typography
          id="referral-code"
          component="code"
          sx={{
            fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
            fontSize: '17px',
            fontWeight: 700,
            color: '#1B2A4A',
            letterSpacing: '0.06em',
            userSelect: 'all',
            flex: 1,
          }}
        >
          {code}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={copied ? <CheckIcon sx={{ fontSize: '14px !important' }} /> : <CopyIcon sx={{ fontSize: '14px !important' }} />}
          onClick={handleCopy}
          sx={{
            fontSize: '13px',
            fontWeight: 500,
            px: 1.75,
            py: 0.5,
            minWidth: 110,
            borderColor: copied ? '#10B981' : '#D1D5DB',
            color: copied ? '#059669' : '#374151',
            bgcolor: copied ? 'rgba(16,185,129,0.06)' : '#FFFFFF',
            borderRadius: '6px',
            textTransform: 'none',
            flexShrink: 0,
            transition: 'all 150ms ease',
            '&:hover': {
              bgcolor: copied ? 'rgba(16,185,129,0.08)' : '#F9FAFB',
              borderColor: copied ? '#10B981' : '#9CA3AF',
            },
          }}
        >
          {copied ? copiedLabel : copyButtonLabel}
        </Button>
      </Box>

      {/* Apply CTA */}
      {config?.applyUrl && (
        <Button
          component="a"
          href={config.applyUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          disableElevation
          endIcon={<ArrowForwardIcon />}
          onClick={() => {
            trackEvent('Referral Apply Click', { partner, url: config.applyUrl });
            gtmOfferClick({ product: 'referral', provider: partner, vertical: 'referral', url: config.applyUrl });
          }}
          sx={{
            width: '100%',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '14px',
            py: 1.25,
            borderRadius: '8px',
            bgcolor: '#1E3A5F',
            color: '#FFFFFF',
            '&:hover': { bgcolor: '#1A3152' },
          }}
        >
          {config.applyLabel}
        </Button>
      )}

      {disclosure && (
        <Typography sx={{ mt: 1.5, fontSize: '11px', color: '#9CA3AF', lineHeight: 1.4 }}>
          {disclosure}
        </Typography>
      )}
    </Box>
  );
};

export default ReferralCodeBlock;
